/**
 * ProductListRenderComponent is a React component that renders a product listing page (PLP) with various features including:
 * - Product filtering and sorting using Algolia's InstantSearch.
 * - Display of product facets (e.g., price, category, color) and the ability to clear filters.
 * - Supports a dynamic breadcrumb navigation for easy category traversal.
 * - Displays products with detailed information, including price, description, and a comparison feature.
 * - Incorporates content from Amplience for top and bottom content sections, as well as frequently asked questions (FAQ).
 * - Provides functionality for comparing multiple products, showing product details in a modal.
 *
 * The component handles responsive design for mobile and desktop views with collapsible facets and sorting options.
 * It also tracks user interactions, such as clicks on the "load more" button or product comparison changes, and integrates with analytics tools for tracking.
 *
 * Props:
 * - `slug`: The product category slug used to filter and display products.
 * - `searchQuery`: The search query used for product filtering.
 * - `categories`: List of product categories available for navigation and filtering.
 * - `facetsConfiguration`: Configuration for the facets available for filtering.
 * - `breadCrumbs`: List of breadcrumbs for navigation, can be dynamic.
 * - `categoryContent`: Content specific to the product category, including descriptions and other details.
 * - `badgeData`: Data for product badges or labels.
 * - `algoliaData`: Data fetched from Algolia for the product listings.
 * - `indexName`: The Algolia index name used for product data.
 * - `amplienceData`: Content from Amplience, including top/bottom content and FAQs.
 *
 * The component uses hooks like `useInstantSearch`, `useClearRefinements`, `useInfiniteHits`, and `useRefinementList` from Algolia's React InstantSearch library to manage search state and render dynamic content.
 *
 * @param {object} props - The props object containing the data for the component.
 * @returns {TSX.Element} - A product listing page with product details, filters, sorting, and comparison options.
 */

'use client';

import { useClearRefinements, useInfiniteHits, useInstantSearch, useRefinementList } from 'react-instantsearch';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { Category } from 'shared/types/product/Category';
import {
  BottomContentDescription,
  FacetConfiguration,
  P2Badge,
  ProductListingContent,
} from '../../product-list-algolia/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { usePathname, useRouter } from 'next/navigation';
import DesktopFacetsUpdate from '../../product-list-algolia/components/desktop-facets/desktop-facets-update';
import { ChevronRightIcon, AdjustmentsHorizontalIcon as FiltersIcon } from '@heroicons/react/24/solid';
import { useFormat } from 'helpers/hooks/useFormat';
import SortBy from '../../product-list-algolia/components/sort-by';
import FaqAmplience from '../../product-list/components/FAQ/faqAmplience';
import ProductListTile from '../../product-list-algolia/components/productList-tile';
import { mapProduct } from 'helpers/algolia/map-product';
import useLocale from 'helpers/hooks/useLocale';
import { PLP_PRODUCT_CLICKED } from 'helpers/constants/events';
import AccumalativeTrace from '../../product-list-algolia/components/accumalative-trace';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import Modal from 'components/commercetools-ui/atoms/modal';
import Price from '../../product-list-algolia/components/Algolia-search-Page/price';
import beforeCompare from 'public/locales/en/product.json';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';

import { attributeMapping } from 'algolia/product-list';
import { RangeSlider } from '../../product-list-algolia/components/facets/rangeSlider';
import { ColourRefinementList } from 'helpers/utils/useRefinementList';
import FacetsSidebar from './FacetsSidebar';
import Link from 'next/link';
import BreadCrumbData from 'public/locales/en/product.json';
import NotFound from '../../product-list-algolia/components/Algolia-search-Page/NotFound';
import { Banner, Color, KeyValue } from '..';
import { PRICE_KEYS } from 'helpers/constants/algolia';

import { useBadgeLoader } from 'helpers/utils/badgeLoader';
import ProductListPageBanner from '../../product-list-algolia/components/productList-banner';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import Loading from 'components/commercetools-ui/atoms/loading';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile } from 'helpers/utils/screensizes';

// Add this style block for the left banner container
const bannerLeftStyle = `
  .banner-container.banner-left {
    height: 100vh;
    display: flex;
    align-items: stretch;
  }
`;

interface Props {
  slug?: string;
  searchQuery?: string;
  categories: Category[];
  facetsConfiguration: Record<string, FacetConfiguration>;
  breadCrumbs?: BreadCrumb[];
  categoryContent?: ProductListingContent;
  p1BadgeData: any;
  p2BadgeData?: P2Badge[];
  algoliaData: any;
  indexName: string;
  amplienceData: any;
  colours: Color[];
  trustBoxCss: TrustBoxCss;
  bannerConfiguration?: Banner;
  keyLabelMap: KeyValue;
}
const ProductListRenderComponent: React.FC<Props> = ({
  slug,
  searchQuery,
  categories,
  facetsConfiguration,
  breadCrumbs = [],
  p1BadgeData,
  p2BadgeData,
  algoliaData,
  indexName,
  amplienceData,
  colours,
  trustBoxCss,
  bannerConfiguration,
  keyLabelMap,
}) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [cartDiscountKey, setCartDiscountKey] = useState<string | null>(null);
  const pathname = usePathname();
  const { result, loggedIn } = useAccount();
  const category = useMemo(() => categories?.find((category) => category?.slug === slug), [categories, slug]);
  const currentCategory = useMemo(() => {
    return (categories?.find((category) => category?.categoryId === category?.categoryId) ?? {}) as Category;
  }, [category?.categoryId]);
  const [ismobile] = useMediaQuery(mobile);
  const { refine: clearRefinements } = useClearRefinements();
  const [isOpen, setIsOpen] = useState(false);
  const { results: algoliaResults, setUiState } = useInstantSearch();
  const clearAllFilters = () => {
    setUiState((prevState) => {
      const currentIndexState = prevState[indexName] || {};

      const newIndexState = {
        ...currentIndexState, // Keep existing properties (e.g. sort-by)
        //query: '',
        refinementList: {}, // Clears all facets
        numericFilters: {}, // Clears numeric filters
        sortBy: undefined, // Clear sort-by filter explicitly
        page: 0, // Reset page to 0
        range: {
          [PRICE_KEYS?.EU]: '', // Reset price range to undefined or default value
          [PRICE_KEYS?.GB]: '',
        },
      };

      const newState = {
        ...prevState,
        [indexName]: newIndexState,
      };
      return newState;
    });

    const pathName = location.pathname;
    let query = location.search;

    // Remove specific query params manually
    query = query
      ?.replace(/([?&])min_price=\d+(\.\d+)?/g, '')
      ?.replace(/([?&])max_price=\d+(\.\d+)?/g, '')
      ?.replace(/([?&])page=\d+/g, '');

    // Clean up leftover connectors
    query = query?.replace(/^&/, '?')?.replace(/&&+/g, '&')?.replace(/\?&/, '?');
    if (query === '?') query = '';
    // Split into segments (filtering out empty strings for easier handling)
    const segments = pathName.split('/').filter(Boolean);

    // Find the index of the "shop-by" and "sort-by" segments (if they exist)
    const shopIndex = segments.indexOf('shop-by');
    const sortIndex = segments.indexOf('sort-by');

    let newSegments = [...segments]; // Start with the full path segments

    // If "shop-by" exists, remove everything from "shop-by" onwards
    if (shopIndex !== -1) {
      newSegments = newSegments.slice(0, shopIndex);
    }

    // If "sort-by" exists, remove it and everything after it
    if (sortIndex !== -1) {
      newSegments = newSegments.slice(0, sortIndex);
    }

    // Reassemble the path, adding a leading slash
    const newPath = '/' + newSegments.join('/');
    const finalUrl = newPath + query;

    router.replace(finalUrl);
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const { hits, showMore } = useInfiniteHits();
  const { locale, fallbackLocale, localeKey } = useLocale();
  const [compareData, setCompareData] = useState(() => {
    const storedData = sessionStorage.getItem('PLP-Compare');
    return storedData ? JSON.parse(storedData) : { query: pathname, products: [] };
  });
  const [open, setOpen] = useState(false);
  const productsToCompare = compareData?.products;
  const userId = loggedIn ? result?.data?.data?.account?.accountId : localStorage.getItem('__anon_id');
  const totalHits = algoliaResults?.nbHits as any;
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const router = useRouter();
  const analytics = new AlgoliaAnalytics(locale);
  const initialViewTracked = useRef(false);

  const initialBannerPosition = useRef(amplienceData?.bannerPosition);
  const initialBannerPositionNumber = useRef(amplienceData?.middlePosition);
  const badgeMap = useBadgeLoader(p1BadgeData); // Parsed variant-codes from CSV link for each badge will be stored in the respective badge response variantCode
  const shouldShowBannerOnDevice = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024; // fallback to desktop for SSR
    const algoliaBannersData = results?.renderingContent?.widgets?.banners?.[0];
    // Check if any device checkbox is selected in amplienceData
    // const hasAmplienceDevice =
    //   amplienceData?.mobile === true || amplienceData?.tablet === true || amplienceData?.desktop === true;

    // // If any amplience checkbox is selected, use those rules
    // if (hasAmplienceDevice) {
    //   if (width < 768 && amplienceData?.mobile && algoliaBannersData) return true;
    //   if (width >= 768 && width < 1024 && amplienceData?.tablet && algoliaBannersData) return true;
    //   if (width >= 1024 && amplienceData?.desktop && algoliaBannersData) return true;
    //   return false;
    // }

    // Fallback: use Algolia rule context (algoliaBannersData?.mobile/tablet/desktop)

    if (width < 768 && algoliaBannersData) return true;
    if (width >= 768 && width < 1024 && algoliaBannersData) return true;
    if (width >= 1024 && algoliaBannersData) return true;

    return false;
  };
  const handleCompareChange = (product: any) => {
    if (compareData?.query !== pathname) {
      setCompareData({ query: pathname, products: [] });
    }

    const isProductInCompareList = compareData?.products?.some((item: any) => item?.productKey === product?.productKey);

    if (isProductInCompareList) {
      setCompareData((prevState: any) => ({
        ...prevState,
        products: prevState?.products.filter((item: any) => item?.productKey !== product?.productKey),
      }));
    } else {
      if (compareData?.products?.length >= 4) {
        alert('You can only compare up to 4 products at a time.');
        return;
      }

      setCompareData((prevState: any) => ({
        ...prevState,
        products: [...prevState?.products, product],
      }));
    }
  };
  const getNextPageUrl = () => {
    const url = new URL(window.location.href);
    const current = parseInt(url.searchParams.get('page') || '1', 10);
    url.searchParams.set('page', String(current + 1));
    return url.pathname + '?' + url.searchParams.toString();
  };
  const button = (
    <button
      onClick={() => {
        showMore();
        handleClickLoadMore(locale);
      }}
      disabled={hits?.length >= totalHits}
      className="mx-auto mt-[90px] block rounded-md bg-primary-black px-48 py-12 text-16 font-bold text-white transition hover:bg-gray-500 disabled:bg-neutral-400 disabled:opacity-0"
    >
      LOAD MORE
    </button>
  );
  const handleClickLoadMore = (locale: string) => {
    const objectIDs = hits.map((item) => item?.objectID);
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleClickLoadMore(objectIDs, userId);
  };
  const removeProductFromSession = (productKey: string) => {
    setCompareData((prevProducts: any) => {
      const products = prevProducts?.products ?? [];

      const updatedProducts = products?.filter((product: any) => product?.productKey !== productKey);

      const updatedCompareProducts = { query: prevProducts?.query, products: updatedProducts };
      sessionStorage.setItem('PLP-Compare', JSON.stringify(updatedCompareProducts));

      return updatedCompareProducts;
    });
  };
  const comparePage = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('previousUrl', pathname);
    }
    router.push(`/compare`);
  };

  const { results } = useInstantSearch();

  const availableFacets =
    results?.renderingContent?.facetOrdering?.facets?.order ?? Object.keys(results?.facets ?? {}) ?? [];
  const renderBanners = (type: string | undefined) => {
    if (!type) return null;

    const algoliaBannersData = results?.renderingContent?.widgets?.banners?.[0];
    const bannerPosition = initialBannerPosition.current ?? bannerConfiguration?.bannerPosition;
    if (
      !bannerConfiguration ||
      bannerPosition !== type ||
      !bannerConfiguration?.isEnabled ||
      !shouldShowBannerOnDevice()
    )
      return null;
    const banner = algoliaBannersData;
    if (!banner) return null;

    return (
      <div className={`banner-container banner-${type}`}>
        <a href={banner?.link?.url ?? '#'} key={banner?.link?.url ?? 'banner-link'}>
          {banner.image?.urls?.map((img: { url: string }) => (
            <img
              key={img?.url}
              src={img?.url}
              alt={banner?.image?.title ?? 'Banner'}
              style={
                type === 'left'
                  ? {
                      width: '100%',
                      height: 'fit-content',
                      objectFit: 'cover',
                      display: 'block',
                      margin: '0 auto',
                      background: '#fff',
                      borderRadius: '0',
                      boxShadow: 'rgba(0,0,0,0.08) 0 2px 8px ',
                    }
                  : { width: '100%', display: 'block' }
              }
            />
          ))}
        </a>
      </div>
    );
  };

  const renderingContent = results?.renderingContent ?? {};

  useEffect(() => {
    if (hits?.length > 0 && !initialViewTracked?.current && !searchQuery) {
      const objectIDs = hits?.map((item) => item?.objectID);
      analytics?.handlePLPViewedProduts(objectIDs, userId, 'PLP: Page Viewed');
      initialViewTracked.current = true; // Mark as tracked
    }
  }, [hits]);

  const handleFacetClick = (locale: string, filter: string[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    if (searchQuery) {
      analytics?.handleSearchPageFilterClick(userId, filter);
    }
  };
  const handleTrackFacetClick = useCallback(
    (facetType: string, value: string) => {
      handleFacetClick(locale, [`${facetType}: ${value}`]);
    },
    [locale],
  );

  if (searchQuery && searchQuery != '' && results?.processingTimeMS > 0 && results?.nbHits === 0) {
    return <NotFound query={searchQuery} />;
  }
  if (!searchQuery && results?.processingTimeMS > 0 && results?.nbHits === 0) {
    if (breadCrumbs?.length > 0 && typeof window !== 'undefined') {
      const breadCrumb = breadCrumbs?.[breadCrumbs?.length - 1];
      const cleanUrl = breadCrumb?.href?.replace(/^\/|\/$/g, '') || '';
      const currentPath = window?.location?.pathname.replace(/^\/|\/$/g, '');

      // Only redirect if current path is different from target
      if (currentPath !== cleanUrl) {
        window?.location?.replace(window?.location?.origin + '/' + cleanUrl);
        return <Loading />;
      } else {
        return (
          <div className="flex justify-center">
            <div>No Product Found</div>
          </div>
        );
      }
    }
  }

  return (
    <>
      {/* Inject the style for the left banner container */}
      <style>{bannerLeftStyle}</style>
      {/* Top Algolia Banner */}
      {renderBanners('top')}
      {cartDiscountKey && (
        <div className="mb-20 flex h-[128px] w-full flex-col items-center justify-center gap-y-20 bg-[#00d195] py-15">
          <p className="font-body text-40 font-bold capitalize leading-[22px]">{cartDiscountKey.replace(/-/g, ' ')}</p>
        </div>
      )}
      {/*Since we are utilizing the Algolia banner, commenting this out.*/}
      {/* {amplienceData?.showbannerContent && <ProductListPageBanner bannerData={amplienceData?.bannerContent} />} */}
      <div className="product-list-algolia min-h-screen max-w-[80rem] p-[1rem] lg:mx-auto">
        {amplienceData?.topContentDescription?.description && (
          <div className="topContentDescription flex justify-center text-center">
            <div className="mx-auto p-15 sm:w-[500px] md:w-[800px]">
              <MarkDownDescription markdownText={amplienceData?.topContentDescription?.description} classname="" />
            </div>
          </div>
        )}
        <div className="relative  sm:px-24 ">
          <div>
            {searchQuery && results?.processingTimeMS > 0 ? (
              <>
                <div className="mb-25 mt-15 flex items-center px-16 pl-15 lg:hidden">
                  <Link href="/" className="text-14">
                    {BreadCrumbData?.BreadCrumb}
                  </Link>
                  <ChevronRightIcon
                    className="mt-2 h-15 w-18 font-bold text-[#127e57]"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                  />
                  <p className="text-14">
                    <span>{searchQuery}</span>
                  </p>
                </div>

                <div className="mb-25 flex items-center justify-center lg:hidden">
                  <span className="font-body text-29 font-semibold leading-[29px]">Search results -</span>
                  <span className="ml-5 mt-3">{searchQuery.toUpperCase()}</span>
                </div>
              </>
            ) : (
              <>
                {(breadCrumbs?.length ?? 0) > 0 && (
                  <div className="px-16">
                    <Breadcrumb
                      useSeparatorNode
                      Separator={<div className="relative top-[2px] text-[#127e57]">{'>'}</div>}
                      className="col-span-12 mb-25 w-fit lg:hidden"
                      dynamicPadding={true}
                      style={{ paddingLeft: '0px' }}
                    >
                      {breadCrumbs
                        ?.map((breadCrumb, index) => {
                          if (breadCrumb.name === 'BRANDS' && breadCrumbs.length > index + 1) {
                            return null;
                          }
                          return breadCrumb;
                        })
                        .filter(Boolean)
                        .map((breadCrumb) => {
                          const isActive = pathname === breadCrumb?.href;
                          return (
                            <SmartLink
                              key={breadCrumb?.href}
                              showLoader={false}
                              href={breadCrumb?.href ?? ''}
                              className={`text-14 font-normal ${
                                isActive ? 'font-semibold text-primary-black' : 'text-secondary-grey'
                              }`}
                            >
                              {breadCrumb?.name}
                            </SmartLink>
                          );
                        })}
                    </Breadcrumb>
                  </div>
                )}

                <div className="mb-25 flex justify-center lg:hidden">
                  <h1 className="font-body text-29 font-semibold leading-[29px]">{amplienceData?.name}</h1>
                </div>
              </>
            )}
          </div>

          <DesktopFacetsUpdate
            facetsConfiguration={facetsConfiguration}
            breadCrumbs={breadCrumbs}
            searchQuery={searchQuery}
            currentCategory={currentCategory}
            isNotFound={results?.nbHits > 0}
            nameOfCategory={amplienceData?.name}
          />

          {
            <div className="clear-filters-container my-10 block px-16 lg:hidden">
              <input
                type="button"
                className="cursor-pointer rounded-md bg-[#1dce8f] p-5 text-13 font-semibold"
                value="Clear Filter"
                onClick={clearAllFilters}
              />
            </div>
          }
          {
            <div className="mobile section flex items-center justify-between px-15 pb-16 lg:hidden">
              <div>
                <button
                  className="flex min-w-[80px] cursor-pointer items-center gap-8 rounded-md border border-transparent bg-white px-12 py-6 text-14 leading-[20px] transition hover:border-gray-500"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <FiltersIcon className="mt-2 w-16 stroke-green-600" />
                  <span className="text-14">
                    {formatProductMessage({ id: 'sortAndFilter', defaultMessage: 'Filter' })}
                  </span>
                </button>
              </div>
              {results.nbHits > 0 && <SortBy isSearch={searchQuery ? true : false} />}
            </div>
          }
          <>
            {searchQuery ? (
              <></>
            ) : (
              <div className="clear-filters-container hidden lg:block">
                <input
                  type="button"
                  className="cursor-pointer rounded-md bg-[#1dce8f] p-5 text-13 font-semibold"
                  value="Clear Filter"
                  onClick={clearAllFilters}
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 lg:mx-auto lg:grid-cols-[20%_80%] lg:gap-8">
              <div className={`section-1 px-15 lg:block lg:px-0 ${isOpen ? 'block' : 'hidden'}`}>
                <FacetsSidebar
                  facets={availableFacets}
                  colors={colours}
                  renderingContent={renderingContent}
                  handleTrackFacetClick={handleTrackFacetClick}
                  keyLabelMap={keyLabelMap}
                />
                {/* Left Algolia Banner */}
                {renderBanners('left')}
              </div>
              <div className="section-2">
                <div className="border-3 grid grid-cols-2 border border-[#f5f4f3] md:grid-cols-3 md:gap-10 md:border-none lg:grid-cols-3 lg:gap-24">
                  {hits?.map((hit, idx) => {
                    const productTile = (
                      <ProductListTile
                        key={hit?.objectID}
                        product={mapProduct(hit, locale, localeKey, fallbackLocale)}
                        isSearchResult={!!searchQuery}
                        onClick={() => gtag('event', PLP_PRODUCT_CLICKED, hit)}
                        handleCompareChange={handleCompareChange}
                        setOpen={setOpen}
                        compareProduct={productsToCompare}
                        p1BadgeData={badgeMap}
                        p2BadgeData={p2BadgeData}
                        slug={slug}
                        queryId={algoliaResults?.queryID}
                        position={idx}
                        trustBoxCss={trustBoxCss}
                      />
                    );

                    // Get the position number from amplienceData or bannerConfiguration
                    const posNumber = initialBannerPositionNumber.current ?? bannerConfiguration?.positionNumber;

                    // Only show middle banner if enabled and position is 'middle'
                    if (
                      bannerConfiguration?.isEnabled &&
                      (initialBannerPosition.current ?? bannerConfiguration?.bannerPosition) === 'middle' &&
                      typeof posNumber === 'number' &&
                      shouldShowBannerOnDevice()
                    ) {
                      // Calculate target position based on device
                      let targetPosition;
                      if (!ismobile) {
                        // Mobile: nearest lower multiple of 2
                        targetPosition = posNumber === 1 ? 0 : Math.floor(posNumber / 2) * 2;
                      } else {
                        // Desktop/Tablet: nearest lower multiple of 3
                        targetPosition = posNumber === 1 || posNumber === 2 ? 0 : Math.floor(posNumber / 3) * 3;
                      }
                      // Insert banner after the computed index (subtract 1 because idx is 0-based)
                      if (idx === targetPosition - 1 && targetPosition >= 0) {
                        return [
                          productTile,
                          <div key="middle-banner" className="col-span-full my-6 w-full px-5">
                            {renderBanners('middle')}
                          </div>,
                        ];
                      }
                      // Special case: insert at the very top (before first product)
                      if (targetPosition === 0 && idx === 0) {
                        return [
                          <div key="middle-banner" className="col-span-full my-6 w-full px-5">
                            {renderBanners('middle')}
                          </div>,
                          productTile,
                        ];
                      }
                    }

                    return productTile;
                  })}
                </div>
                <AccumalativeTrace />

                <SmartLink showLoader={false} reload={false} href={getNextPageUrl()}>
                  {button}
                </SmartLink>

                {productsToCompare?.length !== 0 && (
                  <Modal
                    isOpen={open}
                    style={{ content: { maxWidth: '1200px', height: '572px', bottom: 0, right: 0 } }}
                  >
                    <div className="flex items-center justify-end">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        x="0px"
                        y="0px"
                        width="15"
                        height="15"
                        viewBox="0 0 48 48"
                      >
                        <path fill="#F44336" d="M21.5 4.5H26.501V43.5H21.5z" transform="rotate(45.001 24 24)"></path>
                        <path fill="#F44336" d="M21.5 4.5H26.5V43.501H21.5z" transform="rotate(135.008 24 24)"></path>
                      </svg>
                      <input type="button" onClick={() => setOpen(false)} value={'Close'} />
                    </div>
                    <div>
                      <div className="m-15 flex w-full justify-evenly">
                        <div className={`flex h-[493px] w-[240px] flex-col items-center  border-slate-700`}>
                          <img src={productsToCompare?.[0]?.variants?.[0]?.images?.[0]} className="" />
                          <hr className="my-2 w-full border-t border-slate-500" />

                          <p className="mb-20 mt-10 h-59  w-full text-center text-22 font-bold leading-[22px]">
                            {productsToCompare?.[0]?.name}
                          </p>

                          <div className="mt-20">
                            <Price
                              prices={productsToCompare?.[0]?.standalonePrice}
                              discountedPrice={productsToCompare?.[0]?.discountedPrice}
                            />
                          </div>
                          <hr className="my-2 mt-20 w-full border-t border-slate-500" />
                          <div className="mt-20 flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              x="0px"
                              y="0px"
                              width="15"
                              height="15"
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="#F44336"
                                d="M21.5 4.5H26.501V43.5H21.5z"
                                transform="rotate(45.001 24 24)"
                              ></path>
                              <path
                                fill="#F44336"
                                d="M21.5 4.5H26.5V43.501H21.5z"
                                transform="rotate(135.008 24 24)"
                              ></path>
                            </svg>
                            <input
                              type="button"
                              onClick={() => removeProductFromSession(productsToCompare?.[0]?.productKey)}
                              value={'Remove'}
                              className="cursor-pointer"
                            />
                          </div>
                        </div>

                        {beforeCompare?.beforeCompare?.map((item, index) => {
                          const product = productsToCompare?.[index + 1];

                          return (
                            <div key={index} className="flex h-[493px] w-[240px] flex-col items-center justify-center ">
                              {product ? (
                                <>
                                  <div className={`flex h-[493px] w-[240px] flex-col items-center  border-slate-700`}>
                                    <img src={product?.variants?.[0]?.images?.[0]} className="" />
                                    <hr className="my-2 w-full border-t border-slate-500" />
                                    <p className="mb-20 mt-10 h-59 w-full text-center text-22 font-bold leading-[22px] ">
                                      {product?.name}
                                    </p>
                                    {/* <div className="mb-10 py-7">
                                      <TrustBox id={trustPilotSkuId} showVertical={false} />
                                    </div> */}
                                    <div className="mt-20">
                                      <Price
                                        prices={product?.standalonePrice}
                                        discountedPrice={product?.discountedPrice}
                                      />
                                    </div>
                                    <hr className="my-2 mt-20 w-full border-t border-slate-500" />
                                    <div className="mt-20 flex  items-center">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        x="0px"
                                        y="0px"
                                        width="15"
                                        height="15"
                                        viewBox="0 0 48 48"
                                      >
                                        <path
                                          fill="#F44336"
                                          d="M21.5 4.5H26.501V43.5H21.5z"
                                          transform="rotate(45.001 24 24)"
                                        ></path>
                                        <path
                                          fill="#F44336"
                                          d="M21.5 4.5H26.5V43.501H21.5z"
                                          transform="rotate(135.008 24 24)"
                                        ></path>
                                      </svg>
                                      <input
                                        onClick={() => removeProductFromSession(product?.productKey)}
                                        className="cursor-pointer"
                                        type="button"
                                        value={'Remove'}
                                      />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div
                                  key={index}
                                  className="flex h-[493px] w-[240px] flex-col items-center justify-center border-2 border-dashed border-slate-700"
                                >
                                  <div className="text-42 font-semibold">{item?.number}</div>
                                  <p className="text-center text-16 font-normal">
                                    <span
                                      className="mr-2 cursor-pointer underline underline-offset-4"
                                      onClick={() => setOpen(false)}
                                    >
                                      Select
                                    </span>
                                    {item?.text}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex  justify-end ">
                        <input
                          type="button"
                          className="h-[39px] w-[130px] cursor-pointer bg-[#1DCE8F] text-14"
                          onClick={comparePage}
                          value={'COMPARE NOW'}
                        />
                      </div>
                    </div>
                  </Modal>
                )}
              </div>
            </div>
          </>
        </div>
        {amplienceData?.bottomContent && (
          <>
            {amplienceData?.bottomContent?.length > 0 && (
              <div className="bottomContent-section flex items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
                <div className="w-[930px] items-center">
                  {amplienceData?.bottomContent?.map((content: BottomContentDescription, index: number) => (
                    <div
                      key={index}
                      className={`content-item ${
                        index > 0 && !isExpanded ? 'hidden' : ''
                      } flex flex-col items-center gap-y-6`}
                    >
                      <h2 className="mb-20 mt-16 text-4xl font-bold">{content?.heading}</h2>
                      <MarkDownDescription markdownText={content?.description} classname="" />
                    </div>
                  ))}
                  <div className="mt-10 flex items-center justify-center">
                    {amplienceData?.bottomContent.length > 1 && (
                      <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-x-3">
                        {isExpanded ? (
                          <>
                            <span className="font-body text-16 font-medium">Read Less</span>
                            <span className="text-30 font-normal leading-7 text-[#127e57]">-</span>
                          </>
                        ) : (
                          <>
                            <span className="font-body text-16 font-medium">Read More</span>
                            <span className="text-30 font-normal leading-7 text-[#127e57]">+</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {amplienceData?.frequentlyAskedQuestions && (
          <FaqAmplience faqContent={amplienceData?.frequentlyAskedQuestions} />
        )}
      </div>
      {/* Bottom Algolia Banner */}
      <div className="px-5 py-10">{renderBanners('bottom')}</div>
    </>
  );
};
export default ProductListRenderComponent;
